'use client';

import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { usePathname, useRouter } from '@/src/i18n/navigation';
import { isNearMeSlugCandidate } from '@/src/lib/seo/near-me';
import { stripSrpPageSegment } from '@/src/lib/seo/srp-pagination';

import { FilterContext } from './FilterProvider';
import type { FilterState } from './filterTypes';
import { DEFAULT_FILTERS, FILTER_URL_KEYS } from './filterTypes';
import { deserializeFromUrl, serializeToUrl } from './filterSerializer';
import { applyFilterRemoval } from './applyFilterRemoval';
import { normalizeMoveInDate, validateRange } from './filterValidation';
import { getActiveFilterCount } from './filterLabels';
import { useFilterLabels } from './useFilterLabels';
import { slugDefaultsToFilterState } from './slugDefaultsToFilterState';
import { mergeSlugDefaultsWithExplicit } from './mergeSlugDefaults';
import { useCurrentResolvedSearch } from '@/src/lib/hooks/query/useCurrentResolvedSearch';

function extractFilterKey(query: Record<string, string>): string {
  const parts: Array<string> = [];
  for (const key of FILTER_URL_KEYS) {
    const val = query[key];
    if (val !== undefined) parts.push(`${key}=${val}`);
  }
  return parts.join('&');
}

function pushUrlWithoutServerNavigation(url: string, fallback: (url: string) => void): void {
  if (typeof window === 'undefined') {
    fallback(url);
    return;
  }

  const currentUrl = `${window.location.pathname}${window.location.search}`;
  if (currentUrl === url) return;

  // Next App Router integrates native history updates with usePathname/useSearchParams
  // without issuing a new RSC request. That keeps SRP interactions SPA-fast while
  // still making the URL the source of truth for client state.
  window.history.pushState(null, '', url);
}

// Normalize bounded ranges before they reach `pendingFilters` or the URL —
// deserializeFromUrl normalizes on read, but the overlay bypasses it.
function normalizeFilterRanges(filters: FilterState): FilterState {
  return {
    ...filters,
    price: { ...filters.price, ...validateRange(filters.price.min, filters.price.max) },
    sqft: { ...filters.sqft, ...validateRange(filters.sqft.min, filters.sqft.max) },
    moveInDate: normalizeMoveInDate(filters.moveInDate.start, filters.moveInDate.end),
  };
}

/**
 * App Router sibling of FilterProvider for the SRP.
 *
 * Filters are URL-as-truth: the displayed set is the current slug's defaults
 * (from the BE-resolved state) overridden per-category by the URL query. On
 * interaction `setFilters` materializes the COMPLETE set into the query; only a
 * fully-selected property/listing-type group is omitted (it equals "no filter").
 *
 * Two extras drive the live filter↔slug sync:
 *  - `hasInteracted`: false until the user changes a filter; lets the live search
 *    mirror the SSR request before any interaction (no redundant refetch) and
 *    base-anchor afterwards so the BE can resolve promote AND demote.
 *  - `pendingFilters`: the just-applied filter set, held until `useSearchParams` catches up
 *    with the `pushState` (it lags ~a render). Until then it drives the display and the
 *    `/search/listings` fetch instead of the stale URL — the UI updates immediately and the
 *    fetch fires once with the right filters (without it, the stale render shows old filters
 *    and fires a second, redundant fetch). On a demote it's held longer, until the search
 *    slug resolves: a cleared slug-implied filter can't be expressed on the current slug (the
 *    merge re-adds it). Lives in the persistent search-shell layout, so it survives the nav.
 * Both reset once the URL catches up (slug changed / query reflects the change).
 */
export function FilterProviderAppRouter({ children }: { children: ReactNode }) {
  const router = useRouter();
  const pathname = usePathname() ?? '/';
  const searchParams = useSearchParams();

  const segments = pathname.split('/').filter(Boolean);
  // Near-me SRPs render at a single-segment path (/{slug}) with no area segment, so the slug is
  // segments[0]; every other SRP/HDP route has it at segments[1]. Without this the resolved-state
  // guard below sees searchSlug=undefined and drops the slug's default filters (property type, etc.).
  const isNearMeRoute = segments.length === 1 && isNearMeSlugCandidate(segments[0]);
  const resourceOrMaloneId = segments[0];
  const searchSlug = isNearMeRoute ? segments[0] : segments[1];

  const query = useMemo(() => {
    const q: Record<string, string> = {};
    if (resourceOrMaloneId) q.resourceOrMaloneId = resourceOrMaloneId;
    if (searchSlug) q.searchSlug = searchSlug;
    searchParams?.forEach((value, key) => {
      q[key] = value;
    });
    return q;
  }, [resourceOrMaloneId, searchSlug, searchParams]);

  const hasRouteParams = typeof query.resourceOrMaloneId === 'string';
  const filterKey = extractFilterKey(query);

  // Current slug's implied filters (from /search/listings `resolved.slugDefaults`,
  // seeded per route into the shared cache). Absent (homepage / cache miss) →
  // defaults, so the merge reduces to the plain URL filters.
  const { data: resolved } = useCurrentResolvedSearch();
  // Trust the cached resolved only when it describes the slug we're on: on SRP
  // back/forward it lags the URL until re-hydrate, so a mismatch = stale → query-only.
  // HDP has no search slug and keeps the prior SRP's resolved (the persistent map reads it).
  const isHdpRoute = searchSlug === 'pad' || searchSlug === 'building';
  const slugDefaultsFilters = useMemo(
    () =>
      isHdpRoute || resolved?.resolvedSlug === searchSlug
        ? slugDefaultsToFilterState(resolved?.slugDefaults)
        : DEFAULT_FILTERS,
    [resolved, searchSlug, isHdpRoute],
  );
  const explicitKeys = useMemo(() => {
    const keys = new Set<string>();
    searchParams?.forEach((_value, key) => keys.add(key));
    return keys;
  }, [searchParams]);

  // The URL-derived (source-of-truth) filter set.
  const urlFilters = useMemo(
    () =>
      hasRouteParams
        ? mergeSlugDefaultsWithExplicit(slugDefaultsFilters, deserializeFromUrl(query), explicitKeys)
        : DEFAULT_FILTERS,
    // eslint-disable-next-line react-hooks/exhaustive-deps -- filterKey is a stable derivative of query
    [hasRouteParams, filterKey, slugDefaultsFilters, explicitKeys],
  );

  // Optimistic overlay for the just-applied change; reconciled once the URL catches up.
  // (See the provider docblock for why this exists.)
  const [pendingFilters, setPendingFilters] = useState<FilterState | null>(null);
  useEffect(() => {
    if (pendingFilters && JSON.stringify(pendingFilters) === JSON.stringify(urlFilters)) {
      setPendingFilters(null);
    }
  }, [pendingFilters, urlFilters]);

  const filters = pendingFilters ?? urlFilters;

  // Pre-interaction the live search mirrors SSR; after an interaction it
  // base-anchors. Reset on slug navigation so a freshly-landed page mirrors SSR
  // again (this provider persists across navs in the search-shell layout).
  const [hasInteracted, setHasInteracted] = useState(false);
  const [interactionSeq, setInteractionSeq] = useState(0);
  useEffect(() => {
    setHasInteracted(false);
  }, [pathname]);

  const activeFilterCount = useMemo(() => getActiveFilterCount(filters), [filters]);
  const { getActiveFilterPills } = useFilterLabels();
  const activeFilterPills = useMemo(() => getActiveFilterPills(filters), [filters, getActiveFilterPills]);

  const filterKeyRef = useRef(filterKey);
  filterKeyRef.current = filterKey;

  const buildUrl = useCallback(
    (nextQuery: Record<string, string | undefined>): string => {
      const params = new URLSearchParams();
      for (const [k, v] of Object.entries(nextQuery)) {
        if (v !== undefined && v !== '' && k !== 'resourceOrMaloneId' && k !== 'searchSlug') {
          params.set(k, v);
        }
      }
      const qs = params.toString();
      const basePathname = stripSrpPageSegment(pathname);
      return qs ? `${basePathname}?${qs}` : basePathname;
    },
    [pathname],
  );

  const setFilters = useCallback(
    (raw: FilterState) => {
      const next = normalizeFilterRanges(raw);
      setHasInteracted(true);
      setInteractionSeq((seq) => seq + 1);
      setPendingFilters(next);

      const nonFilterParams: Record<string, string | undefined> = {};
      searchParams?.forEach((value, key) => {
        if (!(FILTER_URL_KEYS as ReadonlyArray<string>).includes(key) && key !== 'page') {
          nonFilterParams[key] = value;
        }
      });

      const writeParams: Record<string, string | undefined> = { ...serializeToUrl(next) };

      pushUrlWithoutServerNavigation(buildUrl({ ...nonFilterParams, ...writeParams }), (url) => {
        router.push(url, { scroll: false });
      });
    },
    [router, buildUrl, searchParams],
  );

  const resetFilters = useCallback(() => {
    setHasInteracted(true);
    setInteractionSeq((seq) => seq + 1);
    setPendingFilters(DEFAULT_FILTERS);
    const nonFilterParams: Record<string, string | undefined> = {};
    searchParams?.forEach((value, key) => {
      if (!(FILTER_URL_KEYS as ReadonlyArray<string>).includes(key) && key !== 'page') {
        nonFilterParams[key] = value;
      }
    });
    pushUrlWithoutServerNavigation(buildUrl(nonFilterParams), (url) => {
      router.push(url, { scroll: false });
    });
  }, [router, buildUrl, searchParams]);

  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const removeFilter = useCallback(
    (key: keyof FilterState, value?: string) => {
      setFilters(applyFilterRemoval(filtersRef.current, key, value));
    },
    [setFilters],
  );

  const contextValue = useMemo(
    () => ({
      filters,
      setFilters,
      resetFilters,
      activeFilterCount,
      activeFilterPills,
      removeFilter,
      hasInteracted,
      interactionSeq,
    }),
    [filters, setFilters, resetFilters, activeFilterCount, activeFilterPills, removeFilter, hasInteracted, interactionSeq],
  );

  return <FilterContext.Provider value={contextValue}>{children}</FilterContext.Provider>;
}
